/** Tree-based content picker for selecting courses, topics, and lessons to revise. */
import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronRight } from 'lucide-react';
import type {
  RevisableCourse,
  RevisableModule,
  RevisableTopic,
  RevisablePersonalLesson,
} from '@formency/shared';
import { Checkbox } from '@/interfaces/learner/ui';

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface LessonRowData {
  id: string;
  title: string;
  reviseStepCount: number;
}

type CheckState = 'checked' | 'unchecked' | 'indeterminate';

function getCheckState(childIds: string[], selected: Set<string>): CheckState {
  const count = childIds.filter((id) => selected.has(id)).length;
  if (count === 0) return 'unchecked';
  if (count === childIds.length) return 'checked';
  return 'indeterminate';
}

function getLessonIds(course: RevisableCourse): string[] {
  return course.modules.flatMap((m) => m.topics.flatMap((t) => t.lessons.map((l) => l.id)));
}

function getModuleLessonIds(mod: RevisableModule): string[] {
  return mod.topics.flatMap((t) => t.lessons.map((l) => l.id));
}

function getTopicLessonIds(topic: RevisableTopic): string[] {
  return topic.lessons.map((l) => l.id);
}

// ─── Checkbox ─────────────────────────────────────────────────────────────────

function TreeCheckbox({ state, onChange }: { state: CheckState; onChange: () => void }) {
  const isChecked = state === 'checked';
  const isIndeterminate = state === 'indeterminate';

  return (
    <Checkbox
      aria-label="Select revision content"
      size="sm"
      isSelected={isChecked}
      isIndeterminate={isIndeterminate}
      onChange={onChange}
    />
  );
}

// ─── Expand toggle ────────────────────────────────────────────────────────────

function ExpandToggle({ expanded, onClick }: { expanded: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-5 w-5 shrink-0 items-center justify-center rounded transition-transform duration-200"
      style={{ color: 'var(--color-text-muted)' }}
    >
      <ChevronRight
        size={14}
        style={{
          transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 200ms ease',
        }}
      />
    </button>
  );
}

// ─── Collapsible section ──────────────────────────────────────────────────────

function Collapsible({ open, children }: { open: boolean; children: React.ReactNode }) {
  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          style={{ overflow: 'hidden' }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── Lesson row ───────────────────────────────────────────────────────────────

function LessonRow({
  lesson,
  isSelected,
  onToggle,
}: {
  lesson: LessonRowData;
  isSelected: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors duration-150"
      style={{ cursor: 'default' }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-surface-muted)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      <div className="w-5" />
      <TreeCheckbox state={isSelected ? 'checked' : 'unchecked'} onChange={onToggle} />
      <span
        className="min-w-0 flex-1 truncate"
        style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}
      >
        {lesson.title}
      </span>
      <span
        className="shrink-0"
        style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}
      >
        {lesson.reviseStepCount}q
      </span>
    </div>
  );
}

// ─── Topic row ────────────────────────────────────────────────────────────────

function TopicRow({
  topic,
  expanded,
  toggle,
  selectedLessonIds,
  toggleLessons,
  toggleLesson,
}: {
  topic: RevisableTopic;
  expanded: Set<string>;
  toggle: (key: string) => void;
  selectedLessonIds: Set<string>;
  toggleLessons: (ids: string[]) => void;
  toggleLesson: (id: string) => void;
}) {
  const key = `t-${topic.id}`;
  const ids = getTopicLessonIds(topic);
  const state = getCheckState(ids, selectedLessonIds);
  const isOpen = expanded.has(key);

  return (
    <div>
      <div
        className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors duration-150"
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-surface-muted)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        <ExpandToggle expanded={isOpen} onClick={() => toggle(key)} />
        <TreeCheckbox state={state} onChange={() => toggleLessons(ids)} />
        <span
          className="min-w-0 flex-1 truncate"
          style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}
        >
          {topic.title}
        </span>
      </div>
      <Collapsible open={isOpen}>
        <div className="ml-6">
          {topic.lessons.map((lesson) => (
            <LessonRow
              key={lesson.id}
              lesson={lesson}
              isSelected={selectedLessonIds.has(lesson.id)}
              onToggle={() => toggleLesson(lesson.id)}
            />
          ))}
        </div>
      </Collapsible>
    </div>
  );
}

// ─── Module row ───────────────────────────────────────────────────────────────

function ModuleRow({
  module: mod,
  expanded,
  toggle,
  selectedLessonIds,
  toggleLessons,
  toggleLesson,
}: {
  module: RevisableModule;
  expanded: Set<string>;
  toggle: (key: string) => void;
  selectedLessonIds: Set<string>;
  toggleLessons: (ids: string[]) => void;
  toggleLesson: (id: string) => void;
}) {
  const key = `m-${mod.id}`;
  const ids = getModuleLessonIds(mod);
  const state = getCheckState(ids, selectedLessonIds);
  const isOpen = expanded.has(key);

  return (
    <div>
      <div
        className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors duration-150"
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-surface-muted)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        <ExpandToggle expanded={isOpen} onClick={() => toggle(key)} />
        <TreeCheckbox state={state} onChange={() => toggleLessons(ids)} />
        <span
          className="min-w-0 flex-1 truncate font-medium"
          style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}
        >
          {mod.title}
        </span>
      </div>
      <Collapsible open={isOpen}>
        <div className="ml-6">
          {mod.topics.map((topic) => (
            <TopicRow
              key={topic.id}
              topic={topic}
              expanded={expanded}
              toggle={toggle}
              selectedLessonIds={selectedLessonIds}
              toggleLessons={toggleLessons}
              toggleLesson={toggleLesson}
            />
          ))}
        </div>
      </Collapsible>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

interface ReviseContentPickerProps {
  courses: RevisableCourse[];
  personalLessons: RevisablePersonalLesson[];
  selectedLessonIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
}

export default function ReviseContentPicker({
  courses,
  personalLessons,
  selectedLessonIds,
  onSelectionChange,
}: ReviseContentPickerProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function toggle(key: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleLessons(ids: string[]) {
    const next = new Set(selectedLessonIds);
    const allSelected = ids.every((id) => next.has(id));
    if (allSelected) {
      ids.forEach((id) => next.delete(id));
    } else {
      ids.forEach((id) => next.add(id));
    }
    onSelectionChange(next);
  }

  function toggleLesson(id: string) {
    const next = new Set(selectedLessonIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  }

  const personalIds = personalLessons.map((l) => l.id);
  const myLessonsKey = 'my-lessons';
  const myLessonsState = getCheckState(personalIds, selectedLessonIds);
  const myLessonsOpen = expanded.has(myLessonsKey);

  return (
    <div className="flex flex-col gap-0.5">
      {/* Personal lessons section */}
      {personalLessons.length > 0 && (
        <div>
          <div
            className="flex items-center gap-2.5 rounded-lg px-2 py-2 transition-colors duration-150"
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-surface-muted)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <ExpandToggle expanded={myLessonsOpen} onClick={() => toggle(myLessonsKey)} />
            <TreeCheckbox state={myLessonsState} onChange={() => toggleLessons(personalIds)} />
            <span
              className="font-semibold"
              style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}
            >
              My Lessons
            </span>
          </div>
          <Collapsible open={myLessonsOpen}>
            <div className="ml-6">
              {personalLessons.map((lesson) => (
                <LessonRow
                  key={lesson.id}
                  lesson={lesson}
                  isSelected={selectedLessonIds.has(lesson.id)}
                  onToggle={() => toggleLesson(lesson.id)}
                />
              ))}
            </div>
          </Collapsible>
        </div>
      )}

      {/* Course tree */}
      {courses.map((course) => {
        const courseKey = `c-${course.id}`;
        const courseIds = getLessonIds(course);
        const courseState = getCheckState(courseIds, selectedLessonIds);
        const isOpen = expanded.has(courseKey);

        return (
          <div key={course.id}>
            <div
              className="flex items-center gap-2.5 rounded-lg px-2 py-2 transition-colors duration-150"
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = 'var(--color-surface-muted)')
              }
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <ExpandToggle expanded={isOpen} onClick={() => toggle(courseKey)} />
              <TreeCheckbox state={courseState} onChange={() => toggleLessons(courseIds)} />
              <span
                className="font-semibold"
                style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}
              >
                {course.title}
              </span>
            </div>
            <Collapsible open={isOpen}>
              <div className="ml-6">
                {course.modules.map((mod) => (
                  <ModuleRow
                    key={mod.id}
                    module={mod}
                    expanded={expanded}
                    toggle={toggle}
                    selectedLessonIds={selectedLessonIds}
                    toggleLessons={toggleLessons}
                    toggleLesson={toggleLesson}
                  />
                ))}
              </div>
            </Collapsible>
          </div>
        );
      })}
    </div>
  );
}
